/**
 * PROPERTY UTILITIES
 *
 * Shared utility functions for property-related operations.
 * Used across portfolio components.
 */

// Australian state codes
export const AUSTRALIAN_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

// State full names mapping
export const STATE_NAMES = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  WA: 'Western Australia',
  SA: 'South Australia',
  TAS: 'Tasmania',
  ACT: 'Australian Capital Territory',
  NT: 'Northern Territory',
};

/**
 * Extract Australian state from an address string
 *
 * @param {string} address - Property address
 * @returns {string|null} State code (e.g., 'NSW') or null if not found
 *
 * @example
 * extractState('123 Smith St, Sydney NSW 2000') // 'NSW'
 * extractState('45 Collins St, Melbourne VIC 3000') // 'VIC'
 */
export function extractState(address) {
  if (!address) return null;

  const upper = address.toUpperCase();

  // Try to match state codes
  for (const state of AUSTRALIAN_STATES) {
    // Match state code with word boundaries to avoid false positives
    const regex = new RegExp(`\\b${state}\\b`);
    if (regex.test(upper)) {
      return state;
    }
  }

  // Fallback: check for full state names
  const stateNameMap = {
    'NEW SOUTH WALES': 'NSW',
    'VICTORIA': 'VIC',
    'QUEENSLAND': 'QLD',
    'WESTERN AUSTRALIA': 'WA',
    'SOUTH AUSTRALIA': 'SA',
    'TASMANIA': 'TAS',
    'AUSTRALIAN CAPITAL TERRITORY': 'ACT',
    'NORTHERN TERRITORY': 'NT',
  };

  for (const [fullName, code] of Object.entries(stateNameMap)) {
    if (upper.includes(fullName)) {
      return code;
    }
  }

  return null;
}

/**
 * Get full state name from code
 *
 * @param {string} stateCode - State code (e.g., 'NSW')
 * @returns {string} Full state name or the original code if not found
 */
export function getStateName(stateCode) {
  return STATE_NAMES[stateCode?.toUpperCase()] || stateCode;
}

/**
 * Extract postcode from an address string
 *
 * @param {string} address - Property address
 * @returns {string|null} 4-digit postcode or null if not found
 */
export function extractPostcode(address) {
  if (!address) return null;

  // Australian postcodes are 4 digits
  const match = address.match(/\b(\d{4})\b/);
  return match ? match[1] : null;
}

/**
 * Extract suburb from an address string (best effort)
 *
 * @param {string} address - Property address
 * @returns {string|null} Suburb name or null
 */
export function extractSuburb(address) {
  if (!address) return null;

  // Try to extract suburb - typically before state code
  // Pattern: "Street, SUBURB STATE POSTCODE"
  const parts = address.split(',');
  if (parts.length >= 2) {
    const lastPart = parts[parts.length - 1].trim();
    // Remove state and postcode
    const withoutPostcode = lastPart.replace(/\b\d{4}\b/, '').trim();
    const withoutState = AUSTRALIAN_STATES.reduce(
      (str, state) => str.replace(new RegExp(`\\b${state}\\b`, 'gi'), ''),
      withoutPostcode
    ).trim();
    return withoutState || null;
  }

  return null;
}

/**
 * Format a property address for display
 *
 * @param {Object} property - Property object with address fields
 * @returns {string} Formatted address
 */
export function formatPropertyAddress(property) {
  if (!property) return '';

  // If fullAddress exists, use it
  if (property.fullAddress) return property.fullAddress;
  if (property.address) return property.address;

  // Build from parts
  const parts = [];
  if (property.streetAddress) parts.push(property.streetAddress);
  if (property.suburb) parts.push(property.suburb);
  if (property.state) parts.push(property.state);
  if (property.postcode) parts.push(property.postcode);

  return parts.join(', ') || 'Unknown Address';
}

/**
 * Calculate geographic concentration by state
 *
 * @param {Array} properties - Array of property objects
 * @returns {Object} State exposure percentages { NSW: 50, VIC: 30, ... }
 */
export function calculateStateConcentration(properties) {
  if (!properties || properties.length === 0) {
    return {};
  }

  const stateCounts = {};
  let total = 0;

  for (const property of properties) {
    const state = property.state || extractState(property.address || property.fullAddress);
    if (state) {
      stateCounts[state] = (stateCounts[state] || 0) + 1;
      total++;
    }
  }

  // Convert to percentages
  const stateExposure = {};
  for (const [state, count] of Object.entries(stateCounts)) {
    stateExposure[state] = Math.round((count / total) * 100);
  }

  return stateExposure;
}

/**
 * Calculate value-weighted geographic concentration
 *
 * @param {Array} properties - Array of property objects with value field
 * @returns {Object} State exposure by value { NSW: { count: 2, value: 1500000, percent: 60 }, ... }
 */
export function calculateValueWeightedConcentration(properties) {
  if (!properties || properties.length === 0) {
    return {};
  }

  const stateData = {};
  let totalValue = 0;

  for (const property of properties) {
    const state = property.state || extractState(property.address || property.fullAddress);
    const value = property.value || property.currentValue || property.purchasePrice || 0;

    if (state) {
      if (!stateData[state]) {
        stateData[state] = { count: 0, value: 0 };
      }
      stateData[state].count++;
      stateData[state].value += value;
      totalValue += value;
    }
  }

  // Add percentages
  for (const state of Object.keys(stateData)) {
    stateData[state].percent = totalValue > 0
      ? Math.round((stateData[state].value / totalValue) * 100)
      : 0;
  }

  return stateData;
}

/**
 * Format currency for Australian dollars
 *
 * @param {number} amount - Amount to format
 * @param {boolean} compact - Use compact notation (e.g., $1.5M)
 * @returns {string} Formatted currency string
 */
export function formatAUD(amount, compact = false) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '$0';
  }

  if (compact && Math.abs(amount) >= 1000000) {
    const sign = amount < 0 ? '-' : '';
    return `${sign}$${(Math.abs(amount) / 1000000).toFixed(1)}M`;
  }

  if (compact && Math.abs(amount) >= 1000) {
    const sign = amount < 0 ? '-' : '';
    return `${sign}$${(Math.abs(amount) / 1000).toFixed(0)}K`;
  }

  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format percentage
 *
 * @param {number} value - Value to format (e.g., 5.5 for 5.5%)
 * @param {number} decimals - Decimal places (default 1)
 * @returns {string} Formatted percentage string
 */
export function formatPercent(value, decimals = 1) {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }
  return `${value.toFixed(decimals)}%`;
}

/**
 * Normalize property fields to handle inconsistent naming conventions
 * Handles: value/currentValue/current_value, loanAmount/loan_amount/loan,
 * weeklyRent/weekly_rent/rent, id/_id, purchasePrice/purchase_price
 *
 * @param {Object} property - Property object with potentially inconsistent field names
 * @returns {Object} Property with normalized field names
 */
export function normalizeProperty(property) {
  if (!property) return null;

  return {
    ...property,
    // Normalize ID
    id: property.id || property._id || `prop-${Date.now()}`,
    // Normalize value fields
    value: property.value ?? property.currentValue ?? property.current_value ?? 0,
    currentValue: property.currentValue ?? property.current_value ?? property.value ?? 0,
    // Normalize loan fields
    loanAmount: property.loanAmount ?? property.loan_amount ?? property.loan ?? 0,
    // Normalize rent fields
    weeklyRent: property.weeklyRent ?? property.weekly_rent ?? property.rent ?? 0,
    // Normalize purchase price
    purchasePrice: property.purchasePrice ?? property.purchase_price ?? 0,
    // Normalize address fields
    address: property.address ?? property.fullAddress ?? '',
    fullAddress: property.fullAddress ?? property.address ?? '',
    // Normalize state (extract from address if not present)
    state: property.state || extractState(property.address || property.fullAddress) || null,
    // Normalize other common fields
    suburb: property.suburb ?? '',
    postcode: property.postcode ?? '',
    propertyType: property.propertyType ?? property.property_type ?? property.type ?? 'House',
    purchaseDate: property.purchaseDate ?? property.purchase_date ?? property.settlementDate ?? property.settlement_date ?? null,
    // Normalize purchaseYear for engine calculations
    purchaseYear: property.purchaseYear ?? property.purchase_year ?? (
      // Extract year from purchaseDate if available
      property.purchaseDate || property.purchase_date || property.settlementDate || property.settlement_date
        ? new Date(property.purchaseDate || property.purchase_date || property.settlementDate || property.settlement_date).getFullYear()
        : null
    ),
    bedrooms: property.bedrooms ?? 0,
    bathrooms: property.bathrooms ?? 0,
    // Property category: 'investment' or 'ppor' (Principal Place of Residence)
    propertyCategory: property.propertyCategory ?? property.property_category ?? 'investment',
    // Convenience flag for PPOR check. The earlier `??` chain was buggy:
    // once any link evaluates to false, `??` short-circuits and skips the
    // rest, so a snake_case-only PPOR flag from the backend never fired.
    // Uses explicit OR semantics — same logic as isPropertyPPOR().
    isPPOR: property.isPPOR === true || property.propertyCategory === 'ppor' || property.property_category === 'ppor',
    // ADR-018 Phase 2: structured cost base + loan detail fields.
    // All optional/nullable. Helpers degrade gracefully when absent.
    costBase: normalizeCostBase(property),
    currentLoanRate: property.currentLoanRate ?? property.current_loan_rate ?? null,
    loanStartDate: property.loanStartDate ?? property.loan_start_date ?? null,
    loanType: normalizeLoanType(property.loanType ?? property.loan_type),
    loanTerm: property.loanTerm ?? property.loan_term ?? null,
    ioExpiry: property.ioExpiry ?? property.io_expiry ?? null,
  };
}

/**
 * Normalize cost-base into the structured v2 shape (ADR-018 Phase 2).
 *
 * Migration semantics:
 *   - Already structured → pass through (filling absent sub-fields with 0/[])
 *   - Legacy single-number → { purchasePrice: that number, rest zero }
 *   - Missing entirely → if property.purchasePrice exists, seed with that;
 *                        otherwise return null (caller handles unknown cost base)
 *
 * The structured shape never silently invents stamp duty / legal / capex —
 * those fields must be entered by the user to be non-zero. The
 * costBaseCompleteness() helper tells the UI whether to show a "refine for
 * accuracy" prompt.
 */
export function normalizeCostBase(property) {
  if (!property) return null;
  const raw = property.costBase;

  // Legacy single-number → structured envelope with purchasePrice only.
  if (typeof raw === 'number' && raw > 0) {
    return {
      purchasePrice: raw,
      stampDuty: 0,
      legalFees: 0,
      otherAcquisition: 0,
      improvements: [],
    };
  }

  // Already structured — fill absent sub-fields defensively.
  if (raw && typeof raw === 'object') {
    return {
      purchasePrice: Number(raw.purchasePrice) || 0,
      stampDuty: Number(raw.stampDuty) || 0,
      legalFees: Number(raw.legalFees) || 0,
      otherAcquisition: Number(raw.otherAcquisition) || 0,
      improvements: Array.isArray(raw.improvements)
        ? raw.improvements
            .filter((i) => i && typeof i === 'object')
            .map((i) => ({
              date: i.date ?? null,
              amount: Number(i.amount) || 0,
              description: i.description ?? '',
            }))
        : [],
    };
  }

  // Seed from purchasePrice if present — better than silently null for CGT math.
  const pp = Number(property.purchasePrice || property.purchase_price);
  if (pp > 0) {
    return {
      purchasePrice: pp,
      stampDuty: 0,
      legalFees: 0,
      otherAcquisition: 0,
      improvements: [],
    };
  }

  return null;
}

/**
 * Normalize loanType input to the canonical enum.
 *
 * Canonical values: 'PI' | 'IO' | 'IO_PI_HYBRID' | null
 * Tolerates common synonyms: 'principal_and_interest', 'interest_only', 'piandio' etc.
 */
export function normalizeLoanType(raw) {
  if (!raw) return null;
  const s = String(raw).toUpperCase().replace(/[\s_-]+/g, '');
  if (s === 'PI' || s === 'PRINCIPALANDINTEREST' || s === 'PANDI') return 'PI';
  if (s === 'IO' || s === 'INTERESTONLY') return 'IO';
  if (
    s === 'IOPIHYBRID' ||
    s === 'IOTOPI' ||
    s === 'HYBRID' ||
    s === 'IOPI'
  ) {
    return 'IO_PI_HYBRID';
  }
  return null; // unknown — caller treats as "not set"
}

/**
 * Validate a property object and log warnings for missing or invalid data.
 * Useful for debugging integration issues between different data sources.
 *
 * @param {Object} property - Property object to validate
 * @param {string} context - Context string for logging (e.g., 'PortfolioDataContext', 'acquisitionTimeline')
 * @returns {{ valid: boolean, warnings: string[] }} Validation result
 */
export function validateProperty(property, context = 'unknown') {
  const warnings = [];
  const identifier = property?.id || property?._id || property?.address || 'unknown property';

  if (!property) {
    return { valid: false, warnings: ['Property is null or undefined'] };
  }

  // Check for value field (critical for calculations)
  const hasValue = property.value !== undefined || property.currentValue !== undefined || property.current_value !== undefined;
  if (!hasValue) {
    warnings.push(`Missing value field (tried: value, currentValue, current_value)`);
  }

  // Check for loan field
  const hasLoan = property.loanAmount !== undefined || property.loan_amount !== undefined || property.loan !== undefined;
  if (!hasLoan) {
    warnings.push(`Missing loan field (tried: loanAmount, loan_amount, loan)`);
  }

  // Check for rent field
  const hasRent = property.weeklyRent !== undefined || property.weekly_rent !== undefined || property.rent !== undefined;
  if (!hasRent) {
    warnings.push(`Missing rent field (tried: weeklyRent, weekly_rent, rent)`);
  }

  // Check for purchaseYear (critical for engine)
  const hasPurchaseYear = property.purchaseYear !== undefined || property.purchase_year !== undefined ||
    property.purchaseDate !== undefined || property.purchase_date !== undefined ||
    property.settlementDate !== undefined || property.settlement_date !== undefined;
  if (!hasPurchaseYear) {
    warnings.push(`Missing purchase year/date field (tried: purchaseYear, purchase_year, purchaseDate, settlement_date)`);
  }

  // Log warnings if any
  if (warnings.length > 0) {
    console.warn(`[${context}] Property validation warnings for "${identifier}":`, warnings);
  }

  return {
    valid: warnings.length === 0,
    warnings,
  };
}

/**
 * Validate and normalize a property, logging any issues.
 * Combines validation with normalization for a single operation.
 *
 * @param {Object} property - Property object
 * @param {string} context - Context string for logging
 * @returns {Object|null} Normalized property or null if invalid
 */
export function validateAndNormalizeProperty(property, context = 'unknown') {
  if (!property) return null;

  // Validate first (logs warnings)
  validateProperty(property, context);

  // Then normalize
  return normalizeProperty(property);
}

/**
 * Safely get property value - handles all field name variants
 * @param {Object} property - Property object
 * @returns {number} Property value (defaults to 0)
 */
export function getPropertyValue(property) {
  if (!property) return 0;
  return property.value ?? property.currentValue ?? property.current_value ?? 0;
}

/**
 * Safely get loan amount - handles all field name variants
 * @param {Object} property - Property object
 * @returns {number} Loan amount (defaults to 0)
 */
export function getPropertyLoan(property) {
  if (!property) return 0;
  return property.loanAmount ?? property.loan_amount ?? property.loan ?? 0;
}

/**
 * Safely get weekly rent - handles all field name variants
 * @param {Object} property - Property object
 * @returns {number} Weekly rent (defaults to 0)
 */
export function getPropertyRent(property) {
  if (!property) return 0;
  return property.weeklyRent ?? property.weekly_rent ?? property.rent ?? 0;
}

/**
 * Safely get property equity
 * @param {Object} property - Property object
 * @returns {number} Equity (value - loan)
 */
export function getPropertyEquity(property) {
  if (!property) return 0;
  // First check if equity is explicitly set
  if (property.equity !== undefined && property.equity !== null) {
    return property.equity;
  }
  // Otherwise calculate from value and loan
  const value = getPropertyValue(property);
  const loan = getPropertyLoan(property);
  return value - loan;
}

/**
 * Safely get property state - extracts from address if not set
 * @param {Object} property - Property object
 * @returns {string|null} State code (e.g., 'NSW') or null
 */
export function getPropertyState(property) {
  if (!property) return null;
  const state = property.state || extractState(property.address || property.fullAddress);
  return state || null;
}

/**
 * Check if property is PPOR (Principal Place of Residence)
 * @param {Object} property - Property object
 * @returns {boolean} True if property is PPOR
 */
export function isPropertyPPOR(property) {
  if (!property) return false;
  return property.isPPOR === true || property.propertyCategory === 'ppor' || property.property_category === 'ppor';
}

/**
 * Filter to get only investment properties (exclude PPOR)
 * @param {Array} properties - Array of property objects
 * @returns {Array} Array of investment properties only
 */
export function getInvestmentProperties(properties) {
  if (!Array.isArray(properties)) return [];
  return properties.filter(p => !isPropertyPPOR(p));
}

/**
 * Filter to get only PPOR properties
 * @param {Array} properties - Array of property objects
 * @returns {Array} Array of PPOR properties only
 */
export function getPPORProperties(properties) {
  if (!Array.isArray(properties)) return [];
  return properties.filter(p => isPropertyPPOR(p));
}

/**
 * Normalize an array of properties
 * @param {Array} properties - Array of property objects
 * @returns {Array} Array of normalized property objects
 */
export function normalizeProperties(properties) {
  if (!Array.isArray(properties)) return [];
  return properties.map(normalizeProperty).filter(Boolean);
}

export default {
  extractState,
  getStateName,
  extractPostcode,
  extractSuburb,
  formatPropertyAddress,
  calculateStateConcentration,
  calculateValueWeightedConcentration,
  formatAUD,
  formatPercent,
  normalizeProperty,
  normalizeProperties,
  validateProperty,
  validateAndNormalizeProperty,
  getPropertyValue,
  getPropertyLoan,
  getPropertyRent,
  getPropertyEquity,
  getPropertyState,
  isPropertyPPOR,
  getInvestmentProperties,
  getPPORProperties,
  AUSTRALIAN_STATES,
  STATE_NAMES,
};
